import {
  Box,
  Button,
  CheckboxInput,
  FormControl,
  FormGroup,
  Input,
  Paragraph,
} from '@deckee/deck-hand';
import { FieldArray, Formik } from 'formik';
import { DateTime } from 'luxon';
import React, { FC } from 'react';
import * as yup from 'yup';
import { AvailableTag } from '../../../../../services/store/modules/incident-report/models';
import CheckListItem from '../CheckListItem/CheckListItem';
import Form from '../Form/Form';

const IncidentDetailsFormSchema = yup.object({
  incidentDateTime: yup.date().required('Date must be provided.'),
  tags: yup.array().of(yup.string().required()).default([]),
});

interface HydratedDetails {
  type: string | null;
  tags: string[];
  incidentDateTime: string | null;
}

const IncidentDetailsForm: FC<{
  onSubmit: (data: any) => Promise<void>;
  tags: AvailableTag[];
  hydratedIncidentDetails: HydratedDetails;
}> = (props) => {
  const { onSubmit, tags, hydratedIncidentDetails } = props;

  // if the hydrated incident details is already a date object, then use it
  // otherwise, create a new date object from the hydrated incident details

  // Check if a variable is already a datetime object with luxon

  let initialDateTime: DateTime = DateTime.now();

  const hydratedDate = hydratedIncidentDetails?.incidentDateTime as any;

  if (hydratedDate instanceof DateTime) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore There is a bug where we are storing incidentDateTime as a date object instead of a string - but it's also working because of this. This is creating issues with types, so it's ok to ignore for now
    initialDateTime = hydratedIncidentDetails?.incidentDateTime as DateTime;
  } else if (hydratedDate instanceof Date) {
    initialDateTime = DateTime.fromJSDate(hydratedDate);
  } else if (hydratedIncidentDetails?.incidentDateTime) {
    initialDateTime = hydratedIncidentDetails.incidentDateTime
      ? DateTime.fromISO(hydratedIncidentDetails.incidentDateTime || '')
      : DateTime.now();
  }

  const initialValues = {
    incidentDateTime: `${initialDateTime.toFormat(
      `yyyy-MM-dd`,
    )}T${initialDateTime.toFormat(`T`)}`,
    tags: hydratedIncidentDetails?.tags ?? [],
  };

  const handleSubmit = (formValues: any) => {
    const newFormValues = {
      ...formValues,
      // datetime-local fields do not have a timezone. Leverage the date class and
      // the iso string function to ensure a timezone is sent
      incidentDateTime: new Date(formValues.incidentDateTime).toISOString(),
    };
    // using the yup schema cast to trim form values where needed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onSubmit(IncidentDetailsFormSchema.cast(newFormValues));
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={IncidentDetailsFormSchema}
      onSubmit={handleSubmit}
    >
      {({
        handleSubmit: formikHandleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
      }) => (
        <Form onSubmit={formikHandleSubmit}>
          <FormGroup heading="Tell us about the incident">
            {Boolean(Array.isArray(tags) && tags.length) && (
              <Box mb={3} borderRadius="12px">
                <Paragraph fontWeight="bold" fontSize="14px">
                  Select any of the following that apply:
                </Paragraph>
                <Box bg="white" p={2} borderRadius="12px">
                  <FieldArray
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    p={3}
                    name="tags"
                    render={(arrayHelpers) =>
                      tags.map((tag) => (
                        <CheckListItem p={2} key={tag.key}>
                          <Box
                            as="label"
                            htmlFor={tag.key}
                            flex="1 1 auto"
                            fontSize="1"
                          >
                            {tag.key}
                          </Box>

                          <CheckboxInput
                            name={tag.key}
                            checked={values.tags.indexOf(tag.key) > -1}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            onChange={({ target: { checked, name } }) => {
                              const currentIndex = values.tags.indexOf(name);
                              if (checked && currentIndex === -1) {
                                arrayHelpers.push(name);
                              } else if (!checked) {
                                arrayHelpers.remove(currentIndex);
                              }
                            }}
                          />
                        </CheckListItem>
                      ))
                    }
                  />
                </Box>
              </Box>
            )}

            <FormControl
              inputMaxWidth="300px"
              label="Date and time the incident was witnessed"
              name="incidentDateTime"
              error={touched.incidentDateTime && errors.incidentDateTime}
              required
            >
              <Input
                type="datetime-local"
                value={values.incidentDateTime}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormControl>
          </FormGroup>
          <FormGroup mt="auto">
            <Button.Group my={3} wide width="100%">
              <Button type="submit">Next</Button>
            </Button.Group>
          </FormGroup>
        </Form>
      )}
    </Formik>
  );
};

IncidentDetailsForm.defaultProps = {};

export default IncidentDetailsForm;
